import { CornerDownLeft, Film, Trash2 } from 'lucide-react';
import { forwardRef, useCallback, useState } from 'react';
import type { ButtonProps } from '@/components/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-ui-primitive/button';
import { ButtonGroup } from '@/components/tiptap-ui-primitive/button-group';
import { Card, CardBody, CardItemGroup } from '@/components/tiptap-ui-primitive/card';
import { Input } from '@/components/tiptap-ui-primitive/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/tiptap-ui-primitive/popover';
import { Separator } from '@/components/tiptap-ui-primitive/separator';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

import './video-popover.scss';

export interface VideoPopoverProps extends Omit<ButtonProps, 'type'> {
	onOpenChange?: (isOpen: boolean) => void;
}

const VideoButton = forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, children, ...props }, ref) => (
		<Button
			type="button"
			className={className}
			variant="ghost"
			role="button"
			tabIndex={-1}
			aria-label="Add video"
			tooltip="Add video"
			ref={ref}
			{...props}
		>
			{children || <Film className="tiptap-button-icon" />}
		</Button>
	)
);
VideoButton.displayName = 'VideoButton';

export const VideoPopover = forwardRef<HTMLButtonElement, VideoPopoverProps>(
	({ onOpenChange, onClick, children, ...buttonProps }, ref) => {
		const { editor } = useTiptapEditor();
		const [isOpen, setIsOpen] = useState(false);
		const [url, setUrl] = useState('');

		const isActive = editor?.isActive('video') ?? false;

		const handleOpenChange = useCallback(
			(nextIsOpen: boolean) => {
				if (nextIsOpen) {
					setUrl('');
				}
				setIsOpen(nextIsOpen);
				onOpenChange?.(nextIsOpen);
			},
			[onOpenChange]
		);

		const handleApply = useCallback(() => {
			if (!editor || !url.trim()) return;
			editor.chain().focus().setVideo(url.trim()).run();
			setUrl('');
			setIsOpen(false);
		}, [editor, url]);

		const handleRemove = useCallback(() => {
			if (!editor) return;
			editor.chain().focus().deleteSelection().run();
			setUrl('');
			setIsOpen(false);
		}, [editor]);

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleApply();
			}
		};

		const handleClick = useCallback(
			(event: React.MouseEvent<HTMLButtonElement>) => {
				onClick?.(event);
				if (event.defaultPrevented) return;
				setIsOpen((prev) => !prev);
			},
			[onClick]
		);

		if (!editor) return null;

		return (
			<Popover open={isOpen} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<VideoButton
						data-active-state={isActive ? 'on' : 'off'}
						aria-label="Add video"
						aria-pressed={isActive}
						onClick={handleClick}
						{...buttonProps}
						ref={ref}
					>
						{children}
					</VideoButton>
				</PopoverTrigger>

				<PopoverContent>
					<Card>
						<CardBody>
							<CardItemGroup orientation="horizontal">
								<Input
									type="url"
									placeholder="Paste YouTube, Vimeo or Loom URL..."
									value={url}
									onChange={(e) => setUrl(e.target.value)}
									onKeyDown={handleKeyDown}
									autoFocus
									autoComplete="off"
									autoCorrect="off"
									autoCapitalize="off"
									className="tiptap-video-input"
								/>

								<ButtonGroup>
									<Button
										type="button"
										onClick={handleApply}
										title="Insert video"
										disabled={!url.trim()}
										variant="ghost"
									>
										<CornerDownLeft className="tiptap-button-icon" />
									</Button>
								</ButtonGroup>

								{isActive && (
									<>
										<Separator />
										<ButtonGroup>
											<Button
												type="button"
												onClick={handleRemove}
												title="Remove video"
												variant="ghost"
											>
												<Trash2 className="tiptap-button-icon" />
											</Button>
										</ButtonGroup>
									</>
								)}
							</CardItemGroup>
						</CardBody>
					</Card>
				</PopoverContent>
			</Popover>
		);
	}
);

VideoPopover.displayName = 'VideoPopover';

export default VideoPopover;
